import { useEffect, useState } from "react";
import { useAudioClock } from "../../hooks/useAudioClock";
import { wordTimingMapper } from "../audio/WordTimingMapper";

interface StreamingTokenizerProps {
  turnId: string;
  tokens: string[];
  finalText: string | null;
  isActive: boolean;
}

const FALLBACK_CHARS_PER_FRAME = 6;

/**
 * Renders a streaming turn's text. Two regimes:
 *  1. While the turn is active and audio is muted/absent, append buffered
 *     tokens immediately (no fake typing).
 *  2. When `finalText` is set AND an audio chunk has timings, ease the visible
 *     prefix forward against the audio playhead via WordTimingMapper.
 */
export function StreamingTokenizer({
  turnId,
  tokens,
  finalText,
  isActive,
}: StreamingTokenizerProps) {
  const playheadMs = useAudioClock();
  const [synthesizedLen, setSynthesizedLen] = useState(0);

  useEffect(() => {
    if (finalText) wordTimingMapper.setText(turnId, finalText);
    return () => {
      if (!isActive) wordTimingMapper.clear(turnId);
    };
  }, [turnId, finalText, isActive]);

  // Active turn without audio: smooth typewriter fallback.
  useEffect(() => {
    if (!isActive || finalText) return;
    const id = window.setInterval(() => {
      setSynthesizedLen((n) => Math.min(n + FALLBACK_CHARS_PER_FRAME, joinedLen(tokens)));
    }, 28);
    return () => window.clearInterval(id);
  }, [isActive, finalText, tokens]);

  if (finalText) {
    const visible = wordTimingMapper.visibleText(turnId, playheadMs);
    if (visible.length > 0 && playheadMs > 0) {
      return <span>{visible}</span>;
    }
    return <span>{finalText}</span>;
  }

  const joined = tokens.join("");
  if (!isActive) return <span>{joined}</span>;
  return <span>{joined.slice(0, Math.max(synthesizedLen, joined.length))}</span>;
}

function joinedLen(tokens: string[]): number {
  let n = 0;
  for (const t of tokens) n += t.length;
  return n;
}
